import { type Column, flexRender, type Table as TanstackTable } from '@tanstack/react-table';

import { notUndefined, useVirtualizer, type Virtualizer } from '@tanstack/react-virtual';
import { type CSSProperties, useEffect, useRef } from 'react';
import { cn } from '../../utils';
import {
  EmptyPlaceholder,
  EmptyPlaceholderDescription,
  EmptyPlaceHolderIcon,
  EmptyPlaceholderTitle,
} from '../empty-placeholder';
import { ScrollArea, ScrollAreaViewport, ScrollBar } from '../scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../table';

type DataTableProps<TData> = {
  /**
   * The table instance returned from useDataTable hook with pagination, sorting, filtering, etc.
   * @type TanstackTable<TData>
   */
  table: TanstackTable<TData> & {
    handleRowClick: (row: TData) => void;
    isRowClickable: boolean;
  };

  /**
   * The floating bar to render at the bottom of the table on row selection.
   * @default null
   * @type React.ReactNode | null
   * @example floatingBar={<TasksTableFloatingBar table={table} />}
   */
  floatingBar?: React.ReactNode;
  emptyPlaceholderTitle?: string;
  emptyPlaceholderDescription?: string;
  onVirtualizerReady?: (virtualizer: Virtualizer<HTMLDivElement, HTMLElement>) => void;
  /**
   * Provide additional props for each rendered data row. Receives the original row data.
   * Useful for attaching event handlers like onMouseEnter, custom data attributes, etc.
   */
  getRowProps?: (row: TData) => React.HTMLAttributes<HTMLTableRowElement>;
} & React.HTMLAttributes<HTMLDivElement>;

const getPinningStyles = <T, V>(column: Column<T, V>): CSSProperties => {
  const isPinned = column.getIsPinned();
  return {
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
  };
};

function getElementHeight() {
  if (typeof window !== 'undefined' && !navigator.userAgent.includes('Firefox')) {
    return (element: HTMLElement) => element?.getBoundingClientRect().height;
  }

  return undefined;
}

export function DataTable<TData>({
  table,
  floatingBar,
  emptyPlaceholderTitle,
  emptyPlaceholderDescription,
  children,
  className,
  onVirtualizerReady,
  getRowProps,
  ...props
}: DataTableProps<TData>) {
  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: table.getRowModel().rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 34,
    measureElement: getElementHeight(),
    overscan: 5,
  });

  useEffect(() => {
    if (onVirtualizerReady) {
      onVirtualizerReady(virtualizer);
    }
  }, [onVirtualizerReady, virtualizer]);

  const items = virtualizer.getVirtualItems();
  const [before, after] =
    items.length > 0
      ? [
          notUndefined(items[0]).start - virtualizer.options.scrollMargin,
          virtualizer.getTotalSize() - notUndefined(items[items.length - 1]).end,
        ]
      : [0, 0];

  const colSpan = table.getAllColumns().length;

  return (
    <div className='w-full space-y-2'>
      {children}
      <ScrollArea>
        <ScrollAreaViewport ref={parentRef}>
          <div className={cn('space-y-4', className)} {...props}>
            <div style={{ height: `${virtualizer.getTotalSize()}px` }}>
              <Table>
                <TableHeader className='sticky top-0 z-10 bg-background/90 backdrop-blur-sm'>
                  {table.getHeaderGroups().map(headerGroup => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map(header => {
                        const { column } = header;
                        const isPinned = column.getIsPinned();
                        const isLastLeftPinned =
                          isPinned === 'left' && column.getIsLastColumn('left');
                        const isFirstRightPinned =
                          isPinned === 'right' && column.getIsFirstColumn('right');
                        return (
                          <TableHead
                            className='[&[data-pinned]]:bg-background/90 [&[data-pinned]]:backdrop-blur-sm'
                            key={header.id}
                            style={{
                              ...getPinningStyles(column),
                              width: column.getSize(),
                            }}
                            data-pinned={isPinned || undefined}
                            data-last-col={
                              isLastLeftPinned ? 'left' : isFirstRightPinned ? 'right' : undefined
                            }
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </TableHead>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {before > 0 && (
                    <TableRow>
                      <TableCell colSpan={colSpan} style={{ height: before }} />
                    </TableRow>
                  )}
                  {table.getRowModel().rows?.length ? (
                    virtualizer.getVirtualItems().map(virtualRow => {
                      const row = table.getRowModel().rows[virtualRow.index];
                      const extraRowProps = getRowProps ? getRowProps(row.original) : undefined;
                      const {
                        onClick: userOnClick,
                        className: userClassName,
                        ...restRowProps
                      } = extraRowProps || {};
                      return (
                        <TableRow
                          key={row.id}
                          data-index={virtualRow.index}
                          ref={node => {
                            virtualizer.measureElement(node);
                          }}
                          style={{
                            height: `${virtualRow.size}px`,
                          }}
                          className={cn(table.isRowClickable && 'cursor-pointer', userClassName)}
                          onClick={e => {
                            table.handleRowClick(row.original);
                            if (userOnClick) {
                              userOnClick(e);
                            }
                          }}
                          {...restRowProps}
                          data-state={row.getIsSelected() && 'selected'}
                          data-testid={row.getIsSelected() && 'selected'}
                        >
                          {row.getVisibleCells().map(cell => {
                            const { column } = cell;
                            const isPinned = column.getIsPinned();
                            const isLastLeftPinned =
                              isPinned === 'left' && column.getIsLastColumn('left');
                            const isFirstRightPinned =
                              isPinned === 'right' && column.getIsFirstColumn('right');

                            return (
                              <TableCell
                                key={cell.id}
                                className='[&[data-pinned]]:bg-background/90 [&[data-pinned]]:backdrop-blur-sm'
                                style={{
                                  ...getPinningStyles(column),
                                  width: column.getSize(),
                                }}
                                data-pinned={isPinned || undefined}
                                data-last-col={
                                  isLastLeftPinned
                                    ? 'left'
                                    : isFirstRightPinned
                                    ? 'right'
                                    : undefined
                                }
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={table.getAllColumns().length}
                        className='h-24 text-center'
                      >
                        <EmptyPlaceholder>
                          <EmptyPlaceHolderIcon name='Text' />
                          <EmptyPlaceholderTitle title={emptyPlaceholderTitle} />
                          <EmptyPlaceholderDescription title={emptyPlaceholderDescription} />
                        </EmptyPlaceholder>
                      </TableCell>
                    </TableRow>
                  )}
                  {after > 0 && (
                    <TableRow>
                      <TableCell colSpan={colSpan} style={{ height: after }} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </ScrollAreaViewport>
        <ScrollBar orientation='horizontal' />
        <ScrollBar orientation='vertical' />
      </ScrollArea>
    </div>
  );
}
